import type { ReactNode } from 'react'

import { useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { gsap, registerGsap, prefersReducedMotion } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface MarqueeProps {
    children: ReactNode
    // pixels travelled per second
    speed?: number
    // reverse direction (left vs right)
    reverse?: boolean
    className?: string
}

// Seamless infinite horizontal marquee. Renders enough copies of the segment to
// cover the viewport, then shifts by exactly one segment width and loops, so the
// reset is invisible.
export function Marquee({ children, speed = 60, reverse = false, className }: MarqueeProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const [copies, setCopies] = useState(3)

    useIsomorphicLayoutEffect(() => {
        const container = containerRef.current
        const track = trackRef.current
        if (!container || !track) return

        registerGsap()

        const seg = track.children[0] as HTMLElement | undefined
        if (!seg) return

        const segWidth = seg.getBoundingClientRect().width
        if (!segWidth) return

        const needed = Math.max(2, Math.ceil(container.offsetWidth / segWidth) + 1)
        if (needed !== copies) {
            setCopies(needed)
            return
        }

        if (prefersReducedMotion()) return

        const ctx = gsap.context(() => {
            gsap.set(track, { x: reverse ? -segWidth : 0 })
            gsap.to(track, {
                x: reverse ? 0 : -segWidth,
                duration: segWidth / speed,
                ease: 'none',
                repeat: -1
            })
        }, containerRef)

        return () => ctx.revert()
    }, [copies, speed, reverse])

    return (
        <div
            ref={containerRef}
            className={className}
            style={{ overflow: 'hidden', width: '100%' }}
        >
            <div
                ref={trackRef}
                style={{ display: 'flex', flexDirection: 'row', willChange: 'transform' }}
            >
                {Array.from({ length: copies }).map((_, i) => (
                    <div key={i} style={{ flex: 'none', display: 'flex' }} aria-hidden={i > 0}>
                        {children}
                    </div>
                ))}
            </div>
        </div>
    )
}
